
/* eslint-disable vue/no-mutating-props */
// Referral source + employer/practice group, shared by the primary (§1, bound to
// state.referral) and new attendees needing guidance (bound to the attendee).
// The group list filters by source type, and the picked group's paying/FA/DPC
// flags are captured so newClientAllowedMethods can gate scheduling.
import { mapGetters } from 'vuex'
import { COPY } from './copy'
import { showReferralGroup, showGroupRelationship, showClientState, OTHER_STATE } from './logic'
import { stateNames } from '~/util/open-states'
import { offeredSourceTypes } from '~/util/source-types'

export default {
    props: {
        referral: { type: Object, required: true },
        required: { type: Boolean, default: false },
        // Where group_relationship is stored. For the primary that's the client
        // person (state.primary), not state.referral; attendees store it on the
        // attendee itself, which is also the `referral` object → defaults to it.
        relationshipTarget: { type: Object, default: null },
        // #727: the open states (FL/RI), for the hint under the state box.
        openStates: { type: Array, default: () => [] },
        // #727: only the PRIMARY's referral asks for the client's state. For an
        // attendee `referral` IS the person, so referral.state is their address
        // -- showing the box or clearing it on a source change would clobber it.
        askState: { type: Boolean, default: false },
    },
    data () {
        return { COPY, OTHER_STATE }
    },
    computed: {
        ...mapGetters(['sourceTypes', 'groupRelationships']),
        // #727: retired sources are not offered; the one on the record still shows.
        offeredSourceTypes () {
            return offeredSourceTypes(this.sourceTypes, this.referral.source_type)
        },
        showState () {
            return this.askState && showClientState(this.referral)
        },
        // A state on record that is not open (a ?state=TX preset, say) reads as
        // Other -- it is not an option of its own any more.
        stateValue () {
            const s = this.referral.state
            if (!s || s === OTHER_STATE || this.openStates.includes(s)) return s || ''
            return OTHER_STATE
        },
        openStateHint () {
            if (!this.openStates.length) return ''
            return COPY.s1.clientStateHint(stateNames(this.openStates))
        },
        relTarget () {
            return this.relationshipTarget || this.referral
        },
        // Group + relationship are required only for group-based sources
        // (employer / financial advisor / DPC). DC-internal and non-paying
        // sources don't carry a group, so they aren't marked/gated.
        fieldsRequired () {
            return this.required && ['EMPLOYER', 'FINANCIAL_ADVISOR', 'DIRECT_PRIMARY_CARE'].includes(this.referral.source_type)
        },
        // Doctor's Choice Internal carries no group; FA / DPC / DC-internal have
        // no "relationship to the group" (#605).
        showGroup () {
            return showReferralGroup(this.referral)
        },
        showRelationship () {
            return showGroupRelationship(this.referral)
        },
        groupLevels () {
            if (this.referral.source_type === 'FINANCIAL_ADVISOR') return ['financial_advisor']
            if (this.referral.source_type === 'DIRECT_PRIMARY_CARE') return ['dpc']
            return [] // employer (all groups) / everything else
        },
        groupLabel () {
            if (this.referral.source_type === 'FINANCIAL_ADVISOR') return COPY.s1.financialAdvisorGroup
            if (this.referral.source_type === 'DIRECT_PRIMARY_CARE') return COPY.s1.dpcGroup
            return COPY.s1.employerGroup
        },
    },
    methods: {
        set (key, val) {
            this.$set(this.referral, key, val)
        },
        setRel (val) {
            this.$set(this.relTarget, 'group_relationship', val)
        },
        onSource (val) {
            this.set('source_type', val)
            // Changing the source invalidates any picked group + its flags.
            this.set('group', null)
            this.set('source_name', null)
            this.setGroupFlags(null)
            // …and any relationship the new source no longer asks for, so a
            // stale value can't ride along in the book payload (#605).
            if (!showGroupRelationship(this.referral)) this.setRel(null)
            // #727: the state and every referral answer belong to the Other path.
            if (this.askState) {
                this.set('state', null)
                this.set('referred_by_type', null)
                this.set('referred_by_client_id', null)
                this.set('referred_by_client_name', '')
                this.set('referred_by_employer_name', '')
                this.set('referred_by_employer_contact', '')
                this.set('sales_followup', true)
                this.set('referred_by_note', '')
            }
        },
        onGroupRaw (obj) {
            this.set('source_name', (obj && obj.name) || null)
            this.setGroupFlags(obj)
        },
        setGroupFlags (obj) {
            this.set('groupPaying', !!(obj && obj.paying))
            this.set('groupFinancialAdvisor', !!(obj && obj.financial_advisor))
            this.set('groupDpc', !!(obj && obj.dpc))
            // The Benefit Bay wind-down marks its former employers in config; the
            // not-eligible card then says why (2026-10-05 request).
            this.set('groupFormerIchra', !!(obj && obj.config && obj.config.former_ichra))
            // #730: a group that resolves the referral to paying / DPC / former
            // Benefit Bay takes the state question away -- and the answer with it.
            if (this.askState && !showClientState(this.referral)) this.set('state', null)
        },
    },
}
